module Steps.Step1.State where

import Common

import API.FormType (formListQuery)
import API.Pagination (Page, fromTestPage, pageItems)
import API.RecentForms (FormSearchQuery(..), RecentForm, searchRecentForms)
import API.Types (DocumentID)
import Control.Monad.Except.Trans (runExceptT)
import Data.Array as Arr
import GraphQL.Join (multiQuery_)
import Internal.SelectionMap (SelectionMap, listSelected)
import Internal.SelectionMap as SM
import Steps.FormPicker (FormPickerState, emptyFormPickerState, initFormPicker)
import Steps.WorkQueue.Cache (WorkQueueCache)
import Steps.WorkQueue.State (WorkQueueState)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Widgets.BetterSelect (selectedItem, setSelected)
import Widgets.Search (SearchState, initSearchState, stateToQuery)

-- | Initial Step 1 loading needs only the runtime-owned work-queue snapshot.
-- | Loaded recent-forms state should keep only the Step 1 fields it owns.
type InitialState =
    { workQueueState :: WorkQueueState
    , workQueueUi :: WorkQueueUiState
    , workQueueCache :: WorkQueueCache
    , preselectForms :: Array DocumentID
    }

type RecentFormState =
    { recentForms :: Page RecentForm
    , selection :: SelectionMap DocumentID RecentForm
    , searchState :: SearchState
    , formPicker :: FormPickerState
    , state :: RecentFormStateFlag
    , searchVersion :: Int
    }

data RecentFormStateFlag = RFNormal | RFLoading | RFError ErrorWrapper

initRecentFormState :: InitialState -> Aff (Either ErrorWrapper RecentFormState)
initRecentFormState initial = runExceptT do
    let searchState = initial.workQueueUi.search
    { recentForms, forms } <- multiQuery_
        { recentForms: searchRecentForms $ rfSearchQuery $
            emptyRecentForms { searchState = searchState }
        , forms: formListQuery
        }
    let formPicker = initFormPicker initial.workQueueState initial.workQueueUi initial.workQueueCache forms
    let preselect = Arr.filter (\f -> f.id `Arr.elem` initial.preselectForms) (pageItems recentForms)

    pure {
        recentForms,
        selection: SM.fromFoldable' preselect,
        searchState,
        formPicker,
        state: RFNormal,
        searchVersion: 0
    }
  where
    emptyRecentForms :: RecentFormState
    emptyRecentForms = {
        recentForms: emptyPage,
        selection: SM.empty,
        searchState: initSearchState,
        formPicker: emptyFormPickerState,
        state: RFNormal,
        searchVersion: 0
    }

emptyPage :: forall a. Page a
emptyPage = fromTestPage []

rfSearchQuery :: RecentFormState -> FormSearchQuery
rfSearchQuery state = FormSearchQuery
    { teacher, semester, class: class', search
    , formIds: Arr.fromFoldable $ map _.id $
        listSelected state.formPicker.workQueueState.forms
    }
  where
    { teacher, semester, class: class', search } =
        stateToQuery state.searchState

applySearchChanged :: SearchState -> RecentFormState -> RecentFormState
applySearchChanged sstate' state =
    state
        { searchState = sstate
        , selection = SM.empty
        , searchVersion = state.searchVersion + 1
        }
  where
    sstate = if selectedItem state.searchState.class /= selectedItem sstate'.class
        then sstate' { semester = setSelected sstate'.semester Nothing }
        else sstate'

applyRecentFormsPage :: Page RecentForm -> RecentFormState -> RecentFormState
applyRecentFormsPage recentForms state =
    state { recentForms = recentForms, state = RFNormal }

startLoading :: RecentFormState -> RecentFormState
startLoading state = state { state = RFLoading }

finishLoading :: RecentFormState -> RecentFormState
finishLoading state = case state.state of
    RFLoading -> state { state = RFNormal }
    _ -> state

setError :: ErrorWrapper -> RecentFormState -> RecentFormState
setError err state = state { state = RFError err }

selectedForms :: RecentFormState -> Array RecentForm
selectedForms state = Arr.fromFoldable $ listSelected state.selection

selectedDocumentIds :: RecentFormState -> Array DocumentID
selectedDocumentIds = map _.id <<< selectedForms
